import { DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import { LngLat } from "mapbox-gl";
import { SnapLineMode } from "mapbox-gl-draw-snap-mode";

import { point as turfPoint } from "fond/turf";

import { snapWithPointPriority } from "./helper";
import { DEFAULT_SNAP_PREVIEW_STYLE, ensurePreviewCircleLayer, removePreviewLayer, setPreviewFeatures } from "./snapPreview";

const SNAP_PREVIEW_SOURCE_ID = "snap-line-preview-source";
const SNAP_PREVIEW_LAYER_ID = "snap-line-preview-layer";

/**
 * Custom SnapLineMode that uses snapWithPointPriority to prioritize
 * snapping to Points over LineStrings/Polygons, and shows a visual
 * preview when snapping occurs.
 */
const CustomSnapLineMode: DrawCustomMode = {
  ...SnapLineMode,

  onSetup: function (opts) {
    const state = SnapLineMode.onSetup?.call(this, opts) ?? {};

    // Setup snap preview layer
    ensurePreviewCircleLayer(this.map, SNAP_PREVIEW_SOURCE_ID, SNAP_PREVIEW_LAYER_ID, DEFAULT_SNAP_PREVIEW_STYLE);

    return state;
  },

  onMouseMove: function (state, e) {
    // Store the original mouse coordinates before snapping
    const originalLng = e.lngLat.lng;
    const originalLat = e.lngLat.lat;

    // Use our custom snap function that prioritizes Points over LineStrings
    const { lng, lat } = snapWithPointPriority(state, e);

    // Store snapped coordinates in state for onClick handler
    state.snappedLng = lng;
    state.snappedLat = lat;

    // Update the event with snapped coordinates
    e.lngLat = new LngLat(lng, lat);

    // Call the base onMouseMove with the snapped coordinates
    const result = SnapLineMode.onMouseMove?.call(this, state, e);

    // Check if coordinates actually changed due to snapping
    const threshold = 0.000001; // Small threshold for floating point comparison
    const didSnap = Math.abs(lng - originalLng) > threshold || Math.abs(lat - originalLat) > threshold;

    // Track if we're at the same snap point as before
    // This handles the case where the cursor is already at a snap point
    const sameAsPrevious =
      state.prevSnappedLng !== undefined &&
      state.prevSnappedLat !== undefined &&
      Math.abs(lng - state.prevSnappedLng) <= threshold &&
      Math.abs(lat - state.prevSnappedLat) <= threshold;

    // If we snapped before and we're still at the same position, keep showing the preview
    const shouldShowPreview = didSnap || (sameAsPrevious && state.wasSnapping);

    // Track snapping state for next frame
    state.prevSnappedLng = lng;
    state.prevSnappedLat = lat;
    state.wasSnapping = didSnap || (sameAsPrevious && state.wasSnapping);

    // Show snap preview if we're snapping or at a snap point from before
    if (state.options?.snap && shouldShowPreview) {
      // Show the snap preview circle at the snapped location
      setPreviewFeatures(this.map, SNAP_PREVIEW_SOURCE_ID, [turfPoint([lng, lat])]);
    } else {
      // Hide the preview if not snapping
      setPreviewFeatures(this.map, SNAP_PREVIEW_SOURCE_ID, []);
    }

    return result;
  },

  onStop: function (state) {
    // Clean up the snap preview layer
    removePreviewLayer(this.map, SNAP_PREVIEW_SOURCE_ID, SNAP_PREVIEW_LAYER_ID);

    // Call parent onStop if it exists
    if (SnapLineMode.onStop) {
      return SnapLineMode.onStop.call(this, state);
    }
  },
};

export default CustomSnapLineMode;
